import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import VirtualizedList from '../../../../../components/VirtualizedList';
import NewFundingTransfersBoxForm from '../forms/NewFundingTransfersBoxForm';
import { Spinner } from '../../../../../components/Spinner';

function NewFundingDetailsTab({
  fundingId,
  fundingEvents,
  fundingStatus,
  consumerIsTest,
  refreshTab,
  setRefreshTab,
  spinner,
  setSpinner,
  refreshFundingData,
}) {
  const [showTransferEvents, setShowTransferEvents] = useState({});

  const getDetails = useCallback(async () => {
    setSpinner(true);
    await refreshFundingData();
    setSpinner(false);
  }, [refreshFundingData, setSpinner]);

  useEffect(() => {
    if (refreshTab === 'details') {
      getDetails();
      setRefreshTab('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTab]);

  const toggleTransferEvent = transferId =>
    setShowTransferEvents(current => ({ ...current, [transferId]: !current[transferId] }));

  const tableColumns = useMemo(
    () => [
      {
        key: 'timestamp',
        header: 'Timestamp',
        width: 230,
        renderValue: value => `${moment.utc(value).local().format('MMM DD YYYY HH:mm (UTCZ)')}`,
      },
      { key: 'description', header: 'Description', width: 180 },
      {
        key: 'amount',
        header: 'Amount',
        width: 150,
        renderValue: value =>
          value !== null && value !== undefined
            ? `$${Number(value)
                .toLocaleString()
                .replace(/\D\d\d$/, '')}`
            : '---',
      },
      { key: 'reason', header: 'Reason', width: 200 },
      {
        key: 'transfers',
        header: 'Transfers',
        width: 600,
        renderValue: (_, row) => (
          <NewFundingTransfersBoxForm
            showTransferEvents={showTransferEvents}
            toggleTransferEvent={toggleTransferEvent}
            event={row}
            fundingId={fundingId}
            fundingStatus={fundingStatus}
            consumerIsTest={consumerIsTest}
            refreshFundingData={refreshFundingData}
            setSpinner={setSpinner}
          />
        ),
      },
    ],
    [showTransferEvents, consumerIsTest, fundingId, fundingStatus, refreshFundingData, setSpinner],
  );

  const getRowHeight = useCallback(
    index => {
      const item = fundingEvents[index];

      const padding = 32;
      const transferDefaultHeight = 200;

      const transferEventHeight = 20;
      const showTransferEventsHeight =
        item.transfers?.reduce(
          (acc, value) =>
            showTransferEvents[value.id]
              ? acc + ((value.events?.length || 0) + 1) * transferEventHeight
              : acc,
          0,
        ) || 0;

      const createManualTranferButton =
        (fundingStatus === 'OPEN' || fundingStatus === 'DUE') &&
        (item.description.includes('Open') || item.description.includes('Top-up')) &&
        item.transfers.every(t => t.status === 'FAILED' || t.status === 'CANCELLED')
          ? 26
          : 0;

      return (
        padding +
        transferDefaultHeight * (item.transfers?.length || 0) +
        showTransferEventsHeight +
        createManualTranferButton
      );
    },
    [fundingEvents, showTransferEvents, fundingStatus],
  );

  const formatCellConditionally = useCallback(
    (_, row) => {
      if (
        (fundingStatus === 'OPEN' || fundingStatus === 'DUE') &&
        (row.description.includes('Open') || row.description.includes('Top-up')) &&
        row.transfers.every(t => t.status === 'FAILED' || t.status === 'CANCELLED')
      ) {
        return { backgroundColor: '#f3d8db' };
      }

      return {};
    },
    [fundingStatus],
  );

  return (
    <AutoSizer>
      {({ height, width }) => (
        <Spinner visible={spinner} overlayWidth={width}>
          <VirtualizedList
            tableRows={fundingEvents}
            tableColumns={tableColumns}
            width={width}
            height={height}
            getRowHeight={getRowHeight}
            customHoverClass="bo-table-row-hover-bg"
            formatCellConditionally={formatCellConditionally}
            rowKey="timestamp"
          />
        </Spinner>
      )}
    </AutoSizer>
  );
}

export default NewFundingDetailsTab;
